import axios, { AxiosRequestConfig } from 'axios';
import { saveAs } from 'file-saver';
import { ActionTree, Module, MutationTree } from 'vuex';
import { namespace } from 'vuex-class';

import { ReportDirectory, ReportFile } from '@/models/report.model';
import { DailyReportState } from '@/store/dailyReport';

import { RootState } from './index';


/**
 *
 * State
 *
 */
export interface ReportState {
  isFetching: boolean;
  value: ReportDirectory;
}

const makeInitialState = (): ReportState => ({
  isFetching: false,
  value: {
    files: [],
    folders: [],
    path: '',
  },
});

const state = makeInitialState;


/**
 *
 * Mutations
 *
 */
const mutations: MutationTree<ReportState> = {
  reset(state) {
    const initialState = makeInitialState();
    for (const key in initialState) {
      if (initialState.hasOwnProperty(key)) {
        state[key] = initialState[key];
      }
    }
  },

  setFetching(state) {
    state.isFetching = true;
  },

  setValue(state, value: ReportDirectory) {
    state.isFetching = false;
    state.value = value;
  },
};


/**
 *
 * Actions
 *
 */
interface ReportActions {
  download: (file: ReportFile) => Promise<void>;
  fetchRoot: () => Promise<void>;
  fetchSubdirectory: (subdirectory: string) => Promise<ReportDirectory>;
}

const actions: ActionTree<ReportState, RootState> = {
  async download(store, file: ReportFile) {
    const options: AxiosRequestConfig = { responseType: 'blob' };
    const companyId = store.rootState.user.user.userInfo.companyId;
    const locationId = store.rootState.location.locationId;
    const params = `companyId=${companyId}&locationId=${locationId}`;
    const response = (await axios.get(`reports/${file.key}?${params}`, options)).data;
    const filename = getFilenameFromKey(file.key);
    saveAs(response, filename);
  },

  async fetchRoot(store) {
    store.commit('setFetching');
    const response = await store.dispatch('fetchSubdirectory', '');
    store.commit('setValue', response);
  },

  async fetchSubdirectory(store, subdirectory: string) {
    const companyId = store.rootState.user.user.userInfo.companyId;
    const locationId = store.rootState.location.locationId;
    const params = `companyId=${companyId}&locationId=${locationId}&subdirectory=${subdirectory}`;
    return (await axios.get <ReportDirectory>(`reports?${params}`)).data;
  },
};


function getFilenameFromKey(key: string) {
  const filenameLastSlash = key.lastIndexOf('/');
  return filenameLastSlash >= 0
    ? key.substr(filenameLastSlash + 1)
    : key;
}



export default {
  namespaced: true,
  actions,
  mutations,
  state,
} as Module<ReportState, RootState>;


// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ReportStore {
  export const Bind = namespace('report');
  export type State <TProp extends keyof ReportState> = ReportState[TProp];
  export type Action <TProp extends keyof ReportActions> = ReportActions[TProp];
}
